import { useRef, useEffect } from "react";
import { sanitizeHtml } from "../lib/sanitizeHtml";

// Familiar word-processor editor over contentEditable. Stores sanitised HTML.
export default function RichTextEditor({ html, disabled, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (html || "")) ref.current.innerHTML = html || "";
  }, [html]);

  const emit = () => { if (ref.current) onChange(sanitizeHtml(ref.current.innerHTML)); };
  const cmd = (c, v) => { document.execCommand(c, false, v); ref.current?.focus(); emit(); };
  const link = () => { const u = window.prompt("Link URL (https://…)"); if (u) cmd("createLink", u); };
  const onPaste = (e) => {
    e.preventDefault();
    const cb = e.clipboardData;
    const h = cb.getData("text/html");
    const t = cb.getData("text/plain");
    const clean = sanitizeHtml(h || `<p>${(t || "").replace(/\n\n+/g, "</p><p>").replace(/\n/g, "<br>")}</p>`);
    document.execCommand("insertHTML", false, clean);
    emit();
  };

  if (disabled) {
    return <div className="prose-tmic policy-html rounded-lg border border-slate-200 bg-white p-4" data-testid="rte-readonly"
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html || "<p style='color:#94a3b8'>No content in this version.</p>") }} />;
  }

  const B = ({ onClick, tid, label, bold }) => (
    <button type="button" data-testid={tid} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className={`h-8 min-w-8 px-2 flex items-center justify-center rounded hover:bg-slate-100 text-slate-700 text-sm ${bold ? "font-black" : "font-semibold"}`}>{label}</button>
  );
  const Sep = () => <span className="w-px h-5 bg-slate-200 mx-1" />;

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 p-1.5 sticky top-0 bg-white z-10">
        <B tid="rte-h2" label="H2" bold onClick={() => cmd("formatBlock", "H2")} />
        <B tid="rte-h3" label="H3" bold onClick={() => cmd("formatBlock", "H3")} />
        <B tid="rte-p" label="¶" onClick={() => cmd("formatBlock", "P")} />
        <Sep />
        <B tid="rte-bold" label="B" bold onClick={() => cmd("bold")} />
        <B tid="rte-italic" label="I" onClick={() => cmd("italic")} />
        <B tid="rte-underline" label="U" onClick={() => cmd("underline")} />
        <Sep />
        <B tid="rte-ul" label="• List" onClick={() => cmd("insertUnorderedList")} />
        <B tid="rte-ol" label="1. List" onClick={() => cmd("insertOrderedList")} />
        <B tid="rte-link" label="Link" onClick={link} />
        <Sep />
        <B tid="rte-undo" label="Undo" onClick={() => cmd("undo")} />
        <B tid="rte-redo" label="Redo" onClick={() => cmd("redo")} />
        <B tid="rte-clear" label="Clear" onClick={() => cmd("removeFormat")} />
      </div>
      <div ref={ref} data-testid="rte-body" contentEditable suppressContentEditableWarning
        onInput={emit} onBlur={emit} onPaste={onPaste}
        className="prose-tmic policy-html min-h-[320px] max-h-[60vh] overflow-y-auto p-4 outline-none focus:ring-1 focus:ring-emerald-300 rounded-b-lg" />
    </div>
  );
}
